import { useRef, useLayoutEffect, useState, useId } from 'react';
import type { HTMLAttributes, ReactNode, CSSProperties } from 'react';
import './SegmentedControl.scss';

type Size = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface SegmentedItem {
  value: string;
  label?: ReactNode;
  disabled?: boolean;
}

interface SegmentedControlProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  data: (string | SegmentedItem)[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  color?: string;
  size?: Size;
  radius?: Size | number;
  orientation?: 'horizontal' | 'vertical';
  fullWidth?: boolean;
  withItemsBorders?: boolean;
  disabled?: boolean;
  transitionDuration?: number;
  transitionTimingFunction?: string;
  readOnly?: boolean;
  name?: string;
}

const FONT_SIZES: Record<Size, string> = {
  xs: '10px',
  sm: '12px',
  md: '14px',
  lg: '16px',
  xl: '18px',
};

const PADDINGS: Record<Size, string> = {
  xs: '3px 10px',
  sm: '5px 14px',
  md: '7px 20px',
  lg: '9px 26px',
  xl: '11px 32px',
};

const RADII: Record<string, string> = {
  xs: '2px',
  sm: '4px',
  md: '8px',
  lg: '16px',
  xl: '100px',
};

const normalize = (data: (string | SegmentedItem)[]): SegmentedItem[] => {
  return data.map(item => (typeof item === 'string' ? { value: item, label: item } : item));
}

const resolveRadius = (radius: Size | number): string => {
  if (typeof radius === 'number') return `${radius}px`;
  return RADII[radius] ?? RADII.xl;
}

export function SegmentedControl({
  data,
  value: valueProp,
  defaultValue,
  onChange,
  color = '#1a1b1e',
  size = 'md',
  radius = 'xl',
  orientation = 'horizontal',
  fullWidth = false,
  withItemsBorders = true,
  disabled = false,
  transitionDuration = 200,
  transitionTimingFunction = 'cubic-bezier(0.4, 0, 0.2, 1)',
  readOnly = false,
  name,
  className,
  style,
  ...rest
}: SegmentedControlProps) {
  const uid = useId();
  const groupName = name ?? uid;
  const isVertical = orientation === 'vertical';

  const normalized = normalize(data);
  const isControlled = valueProp !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue ?? normalized[0]?.value ?? '');
  const value = isControlled ? valueProp! : internalValue;

  const containerRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [ready, setReady] = useState(false);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const update = () => {
      const activeIndex = normalized.findIndex(d => d.value === value);
      const controls = container.querySelectorAll<HTMLElement>('[data-sc-control]');
      const el = controls[activeIndex];
      if (el) {
        setIndicator({ x: el.offsetLeft, y: el.offsetTop, width: el.offsetWidth, height: el.offsetHeight });
        setReady(true);
      }
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(container);
    return () => ro.disconnect();
  }, [value, data, orientation]);

  const activeIndex = normalized.findIndex(d => d.value === value);

  const handleChange = (newValue: string) => {
    if (disabled || readOnly) return;
    if (!isControlled) setInternalValue(newValue);
    onChange?.(newValue);
  };

  const cssVars: CSSProperties = {
    '--sc-color': color,
    '--sc-font-size': FONT_SIZES[size],
    '--sc-padding': PADDINGS[size],
    '--sc-radius': resolveRadius(radius),
    '--sc-duration': `${transitionDuration}ms`,
    '--sc-easing': transitionTimingFunction,
  } as CSSProperties;

  const indicatorStyle: CSSProperties = isVertical
    ? { transform: `translateY(${indicator.y}px)`, height: indicator.height }
    : { transform: `translateX(${indicator.x}px)`, width: indicator.width };

  const rootClass = [
    'sc',
    isVertical && 'sc--vertical',
    fullWidth && 'sc--full-width',
    disabled && 'sc--disabled',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={containerRef}
      role="radiogroup"
      className={rootClass}
      style={{ ...cssVars, ...style }}
      {...rest}
    >
      <div
        className={`sc__indicator${ready ? ' sc__indicator--ready' : ''}`}
        style={indicatorStyle}
      />

      {normalized.map((item, i) => {
        const isActive = item.value === value;
        const isItemDisabled = disabled || item.disabled;
        const showDivider = withItemsBorders && i > 0;
        const hideDivider = isActive || activeIndex === i - 1;

        return (
          <label
            key={item.value}
            data-sc-control
            className={[
              'sc__control',
              isActive && 'sc__control--active',
              isItemDisabled && 'sc__control--disabled',
            ].filter(Boolean).join(' ')}
          >
            <input
              type="radio"
              className="sc__input"
              name={groupName}
              value={item.value}
              checked={isActive}
              disabled={isItemDisabled}
              onChange={() => handleChange(item.value)}
              aria-label={typeof item.label === 'string' ? item.label : item.value}
            />
            {showDivider && (
              <span
                className={`sc__divider${hideDivider ? ' sc__divider--hidden' : ''}`}
                aria-hidden="true"
              />
            )}
            <span className="sc__label">{item.label ?? item.value}</span>
          </label>
        );
      })}
    </div>
  );
}
